import { createAsyncThunk, current } from '@reduxjs/toolkit';
import { createFetch, createSchema } from '@better-fetch/fetch';

// types
import { ContextMenuItem } from '../menus/context-menu';
import {
  ApiError,
  ApiErrorSchema,
  createAppAsyncThunk,
  CavernFileType,
  DataItemType,
} from '../../utils/types';

// functions
import { useTranslation } from 'react-i18next';
import { apiPrefix, extractFileFromObsID } from '../../utils/functions';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { store } from '../../app/store';

// import state definition and functions.
import {
  ProjectState,
  setLatestQueryID,
  setLoadingCavernFiles,
  setLoadingRucioFiles,
  setProjectItems,
  setRenamingFile,
  setUserItems,
} from './project-slice';
import { GlobalState } from '../../app/global-slice';
import { getSelectedCanfar } from '../../app/canfar-selectors';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C O N S T A N T S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  F U N C T I O N S
//
//  --------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  Cancels the current cavern operation.
//
//  ------------------------------------------------------------

export function cancelQuery() {
  //
  // get state.
  const state: any = store.getState();

  // set the latest query ID to -1.
  store.dispatch(setLatestQueryID(-1));
  store.dispatch(setLoadingCavernFiles(false));
  store.dispatch(setRenamingFile(false));
  //
} // cancelQuery

//  ------------------------------------------------------------
//
//  An asynchronous function that retrieves a list of project
//  files from the TAP server.
//
//  ------------------------------------------------------------

export const getProjectFiles = createAsyncThunk(
  'file_browser/getProjectFiles',
  async (_, thunkAPI) => {
    //
    const stateFetch: any = thunkAPI.getState();
    const state: ProjectState = stateFetch.project;
    const globalState: GlobalState = stateFetch.global;

    store.dispatch(setLoadingRucioFiles(true));
    store.dispatch(setProjectItems([]));

    let urlCommand: string = apiPrefix() + '/v1/data/get_data?';

    // add the data-product subtype.
    urlCommand =
      urlCommand + 'project=' + encodeURIComponent(globalState.project?.name ?? '') + '&';

    // add page and page size.
    urlCommand = urlCommand + 'page=0&page_size=1000';

    try {
      //
      const apiResult = await fetch(urlCommand, { credentials: 'include' });
      const returnedJson = await apiResult.json();

      store.dispatch(setLoadingRucioFiles(false));

      // get response.
      let results: any = [];
      if (returnedJson.results !== undefined) {
        results = returnedJson.results;
      }

      // Handle empty results
      if (results.length === 0) {
        //
        // store empty results.
        store.dispatch(setProjectItems([]));
        return;
        //
      }

      // loop over all the rows.
      let dataItems: DataItemType[] = [];
      if (Array.isArray(results) === true) {
        //
        results.forEach((element: any) => {
          //
          let fileDetails: {
            namespace: string;
            filename: string;
          } = extractFileFromObsID({
            obsID: element.obs_id ?? '',
          });
          let newDataItem: DataItemType = {
            id: -1,
            namespace: fileDetails.namespace,
            filename: fileDetails.filename,
            obs_publisher_did: element.obs_publisher_did
              ? element.obs_publisher_did
              : undefined,
            did_type: element.dataproduct_type ? element.dataproduct_type : undefined,
            target_name: element.target_name ? element.target_name : undefined,
          };
          dataItems.push(newDataItem);
          //
        });
        //
      }

      // store these data items.
      store.dispatch(setProjectItems(dataItems));
      //
    } catch (e) {
      console.error('Error fetching cone search data:', e);
    }
    //
  },
  //
); // getProjectFiles

//  ------------------------------------------------------------
//
//  An asynchronous function that retrieves a list of user-
//  generated project files from cavern.
//
//  ------------------------------------------------------------

export const getUserProjectFiles = createAsyncThunk(
  'file_browser/getUserProjectFiles',
  async (_, thunkAPI) => {
    //
    let stateFetch: any = thunkAPI.getState();
    const state: ProjectState = stateFetch.project;
    const globalState: GlobalState = stateFetch.global;

    let items: CavernFileType[] = [];

    // store the query ID. we need to change that it hasn't changed before we handle the
    // query response.
    let queryID: number = Date.now();
    store.dispatch(setLoadingCavernFiles(true));
    store.dispatch(setLatestQueryID(queryID));
    store.dispatch(setUserItems([]));

    // Build storage path
    const path: string = '';
    const url: string = getSelectedCanfar(store.getState())?.url ?? '';

    // Build query params
    const params = new URLSearchParams();
    params.append('storage_type', 'user');
    if (url !== '') {
      params.append('url', url);
    }
    params.append('path', `/home/${globalState.preferredUsername}/${path}`);

    const urlCommand = `${apiPrefix()}/v1/storage/list_items?${params.toString()}`;
    const $fetch = createFetch({
      retry: {
        type: 'linear',
        attempts: 3,
        delay: 1000,
        retryOn: (error: unknown, res: Response | undefined) =>
          !res || res.status === 504,
        resolveBodyOnly: false,
      },
    });

    try {
      //
      const { data, error } = await $fetch<
        {
          vosUri?: string;
          items?: {
            filename: string;
            file_type: string;
            modified_date: string;
            length: number;
          }[];
        },
        ApiError
      >(urlCommand, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        errorSchema: ApiErrorSchema,
      });

      // get the latest query ID, and check it hasn't changed.
      stateFetch = thunkAPI.getState();
      if (stateFetch.project.latestQueryID !== queryID) {
        return;
      }

      if (error) {
        console.warn('Error message:', JSON.stringify(error.detail));
      }

      // convert the response into a usable format.
      items =
        data?.items
          ?.filter((item: any) => {
            return (
              !item.filename.startsWith('.') && item.file_type.toUpperCase() === 'FILE'
            );
          })
          .map((item: any) => ({
            filename: item.filename,
            fileType: item.file_type,
            modifiedDate: item.modified_date,
            length: item.length,
          })) ?? [];
      //
    } catch (e) {
      console.error('Error loading storage:', e);
    }

    // store the updated list.
    store.dispatch(setUserItems(items));
    store.dispatch(setLoadingCavernFiles(false));
    //
  },
  //
); // getUserProjectFiles
